import { useState } from 'react';
import { Badge } from '@/components/primitives/Badge';
import { ExternalLinkIcon } from '@/components/primitives/ExternalLinkIcon';
import type { JiraProjectGroup } from '@/data/employeeDetail';
import styles from './JiraWorklogTable.module.css';

/**
 * Task 139 — "Time in Jira" section table. Mirrors the shape of
 * `ClientProjectTable` on the employee detail page: collapsible
 * groups keyed by `(credentialId, projectKey)`, issue rows under
 * each group, right-aligned mono Hours / Entries / % columns.
 *
 * Props:
 *   - `groups` — pre-aggregated from the server; total count and
 *     hours per group are already computed.
 *   - `showCredentialLabels` — when true (multi-credential tenant or
 *     two credentials with overlapping project keys) the project row
 *     shows the credential label as a neutral pill. When false
 *     (single-credential tenant) it's suppressed to reduce visual
 *     noise.
 *
 * Issue rows link out to `{siteUrl}/browse/{issueKey}` in a new tab
 * with `rel="noopener noreferrer"` and the external-link icon
 * indicator (`ExternalLinkIcon` primitive).
 *
 * Status badge colour is keyed on `status_category`
 * (`new` / `indeterminate` / `done`) — Atlassian's tri-state. No
 * Kanjoy-side mappings of "in progress" etc. per the locked
 * design decision in task 139.
 *
 * Archived projects / issues render muted with a `title="Archived
 * in Jira"` tooltip.
 */
export function JiraWorklogTable({
  groups,
  showCredentialLabels,
}: {
  groups: readonly JiraProjectGroup[];
  showCredentialLabels: boolean;
}) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(groups.map((g) => [g.key, true])),
  );
  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totalHours = groups.reduce((s, g) => s + g.hours, 0);
  const totalEntries = groups.reduce((s, g) => s + g.entries, 0);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Project / Issue</th>
            <th>Status</th>
            <th className={styles.alignRight}>Hours</th>
            <th className={styles.alignRight}>Entries</th>
            <th>% of Jira total</th>
          </tr>
        </thead>
        {groups.map((group) => {
          const isOpen = openGroups[group.key] ?? false;
          const groupPct = totalHours > 0 ? Math.round((group.hours / totalHours) * 100) : 0;
          return (
            <tbody key={group.key}>
              <tr
                className={`${styles.groupRow} ${group.projectArchived ? styles.archivedRow : ''}`}
                onClick={() => toggleGroup(group.key)}
              >
                <td>
                  <button
                    type="button"
                    className={styles.chevronBtn}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Collapse group' : 'Expand group'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(group.key);
                    }}
                  >
                    <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>
                      &#9654;
                    </span>
                  </button>
                  <span
                    className={styles.projectName}
                    title={group.projectArchived ? 'Archived in Jira' : undefined}
                  >
                    {group.projectName}
                  </span>
                  {showCredentialLabels && (
                    <span className={styles.credentialLabel}>{group.credentialLabel}</span>
                  )}
                </td>
                <td />
                <td className={`${styles.mono} ${styles.alignRight}`}>{group.hours.toFixed(1)}h</td>
                <td className={`${styles.mono} ${styles.alignRight}`}>
                  {group.entries.toLocaleString()}
                </td>
                <td>
                  <BarCell pct={groupPct} muted={group.projectArchived} />
                </td>
              </tr>
              {isOpen &&
                group.issues.map((issue) => {
                  const issuePct =
                    totalHours > 0 ? Math.round((issue.hours / totalHours) * 100) : 0;
                  return (
                    <tr
                      key={issue.issueKey}
                      className={`${styles.issueRow} ${issue.issueArchived ? styles.archivedRow : ''}`}
                    >
                      <td className={styles.issueCell}>
                        <a
                          className={styles.issueLink}
                          href={issue.browseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={issue.issueArchived ? 'Archived in Jira' : undefined}
                        >
                          <span className={styles.issueKeyText}>{issue.issueKey}</span>
                          <ExternalLinkIcon size={12} />
                        </a>
                        <span className={styles.issueSummary}>{issue.summary}</span>
                      </td>
                      <td>
                        <Badge variant={badgeVariantFor(issue.statusCategory)}>
                          {issue.statusName}
                        </Badge>
                      </td>
                      <td className={`${styles.mono} ${styles.alignRight}`}>
                        {issue.hours.toFixed(1)}h
                      </td>
                      <td className={`${styles.mono} ${styles.alignRight}`}>
                        {issue.entries.toLocaleString()}
                      </td>
                      <td>
                        <BarCell pct={issuePct} muted={issue.issueArchived} />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          );
        })}
        <tfoot>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td />
            <td className={`${styles.mono} ${styles.alignRight}`}>
              <strong>{totalHours.toFixed(1)}h</strong>
            </td>
            <td className={`${styles.mono} ${styles.alignRight}`}>
              <strong>{totalEntries.toLocaleString()}</strong>
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function BarCell({ pct, muted }: { pct: number; muted?: boolean }) {
  return (
    <div className={styles.barCellWrap}>
      <div className={styles.barCell}>
        <div className={styles.barFill} style={{ width: `${pct}%`, opacity: muted ? 0.5 : 1 }} />
      </div>
      <span className={styles.barPct}>{pct}%</span>
    </div>
  );
}

// Map Atlassian's tri-state to the existing Badge variants so we
// don't introduce new design tokens. Badge currently supports
// 'billable' | 'general' | 'enabled' | 'disabled'; `indeterminate`
// uses 'general' (orange-ish), `done` uses 'enabled' (green-ish),
// `new` uses 'disabled' (muted neutral) as the closest semantic fit.
// If the Badge palette grows, revisit.
function badgeVariantFor(
  category: 'new' | 'indeterminate' | 'done',
): 'enabled' | 'disabled' | 'general' {
  switch (category) {
    case 'done':
      return 'enabled';
    case 'indeterminate':
      return 'general';
    case 'new':
    default:
      return 'disabled';
  }
}
